import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Common/FormElements/Button/Button";
import { useDispatch } from "react-redux";
import { closeCenteredModal } from "../../REDUX/actions/centeredModal";
import Select from "../Common/FormElements/Select/Select";
import API from "../../Api/Api";
import { API_END_POINT } from "../../Api/EndPoints";
import moment from "moment";
import ArrowIcon from "../Common/icons/ArrowIcon";
import { saveAs } from "file-saver";
import { Spinner } from "react-bootstrap";

const AccountantInvoice = ({ combinedInvoices }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [invoices, setInvoices] = useState([]);
  const [optionData, setOptionData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState();
  const [formData, setFormData] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);

  const handleChange = (e, index, id) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      return prev.map((item, idx) => {
        if (idx !== index) return item;

        return {
          ...item,
          packings: {
            ...(item?.packings || {}),
            [id]: {
              ...(item?.packings?.[id] || {}),
              [name]: value,
            },
          },
        };
      });
    });
  };

  const handleChangeTotal = (e, type, invoiceId) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      return prev.map((item) => {
        if (+item?.id !== +invoiceId) return item;

        if (type === "delivery") {
          return {
            ...item,
            delivery: {
              ...(item?.delivery || {}),
              [name]: value,
            },
          };
        } else if (type === "tva") {
          return {
            ...item,
            tva: {
              ...(item?.tva || {}),
              [name]: value,
            },
          };
        } else if (type === "total") {
          return {
            ...item,
            total: {
              ...(item?.total || {}),
              [name]: value,
            },
          };
        } else {
          return item;
        }
      });
    });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const { data } = await API.post(
        `${API_END_POINT}api/generate-accountant-invoices`,
        {
          data: {
            invoices: formData.filter(({ type }) => type === "facture"),
            acompteFactures: formData.filter(
              ({ type }) => type === "acompte-facture"
            ),
          },
        }
      );
      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
      const today = moment().format("DD-MM-YYYY");
      saveAs(blob, `Factures AB WELLNESS ${today}.csv`);
      dispatch(closeCenteredModal());
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkIfSelected = (index) => {
    const selected =
      formData[index]?.packings &&
      Object.values(formData[index]?.packings).every(
        (packing) => packing?.accountNumber && packing?.clientNumber
      ) &&
      (!formData[index]?.hasDelivery ||
        (formData[index]?.delivery?.accountNumber &&
          formData[index]?.delivery?.clientNumber)) &&
      formData[index]?.tva?.accountNumber &&
      formData[index]?.tva?.clientNumber &&
      formData[index]?.total?.accountNumber &&
      formData[index]?.total?.resellerNumber &&
      formData[index]?.total?.clientNumber;
    return selected;
  };

  const checkIfAllSelected = () => {
    const allSelected = formData.every(
      (item) =>
        item?.packings &&
        Object.values(item.packings).every(
          (packing) => packing?.accountNumber && packing?.clientNumber
        ) &&
        (!item?.hasDelivery ||
          (item?.delivery?.accountNumber && item?.delivery?.clientNumber)) &&
        item?.tva?.accountNumber &&
        item?.tva?.clientNumber &&
        item?.total?.accountNumber &&
        item?.total?.resellerNumber &&
        item?.total?.clientNumber
    );
    setIsDisabled(!allSelected);
  };

  useEffect(() => {
    checkIfAllSelected();
  }, [formData]);

  useEffect(() => {
    if (!combinedInvoices?.length > 0) return;
    (async function () {
      setIsLoadingData(true);
      try {
        const { data } = await API.post(
          `${API_END_POINT}api/accountant-invoices`,
          {
            data: {
              invoiceIds: combinedInvoices
                ?.filter(({ type }) => type === "facture")
                ?.map(({ id }) => id),
              acompteFactureIds: combinedInvoices
                ?.filter(({ type }) => type === "acompte-facture")
                ?.map(({ id }) => id),
            },
          }
        );
        setInvoices(data?.invoices);
        setFormData(
          data?.invoices?.map(({ id, type, commande }) => ({
            id,
            hasDelivery: true,
            delivery: commande?.deliveryAccount,
            tva: commande?.tvaAccount,
            total: commande?.totalAccount,
            type,
            packings: commande?.packings?.reduce((acc, packing) => {
              acc[packing.id] = {
                accountNumber: packing?.accountNumber,
                clientNumber: packing?.clientNumber,
              };
              return acc;
            }, {}),
          }))
        );
        setOptionData({
          resellerCodeOptions: data?.resellerCodeOptions,
          accountOptions: data?.accountOptions,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingData(false);
      }
    })();
  }, [combinedInvoices]);

  return (
    <div>
      <p className="f15 sBold text-center mb-4">
        {t("Accountant invoices")} ({formData?.length})
      </p>

      {isLoadingData && (
        <div className="w-100 d-flex justify-content-center py-4 m-auto">
          <Spinner />
        </div>
      )}
      {invoices?.map((invoice, index) => {
        return (
          <div className="d-flex flex-column gap-3" key={invoice.id}>
            <div
              onClick={() =>
                setCurrentInvoice(
                  currentInvoice && invoice.id === currentInvoice?.id
                    ? null
                    : invoice
                )
              }
              className={`w-100 text-start py-2 px-3 bg-${
                checkIfSelected(index) ||
                (currentInvoice && invoice.id === currentInvoice?.id)
                  ? "skyBlue"
                  : "dimGrey"
              } my-1 br4 d-flex justify-content-start cursor-pointer gap-4`}
            >
              <p className="f14 sBold text-brightGrey mb-0">
                {t("Invoice number")} :{" "}
                <span>{`FA${invoice?.number || ""}`}</span>
              </p>
              <p className="f14 sBold text-brightGrey mb-0">
                {t("Invoice date")} :{" "}
                <span>
                  {invoice?.date
                    ? moment(invoice?.date).format("DD/MM/YYYY")
                    : ""}
                </span>
              </p>
              <p className="f14 sBold text-brightGrey mb-0">
                {t("Order number")} : <span>{invoice?.commande?.number}</span>
              </p>
              <div className="ms-auto">
                <ArrowIcon />
              </div>
            </div>
            {currentInvoice?.id === invoice.id && (
              <div className="d-flex flex-column">
                {currentInvoice?.commande?.packings.map((packing) => {
                  const name = `${packing.name ?? packing?.model?.name ?? ""}${
                    packing.cuve ? ` - ${packing.cuve}` : ""
                  }${packing.panel ? ` - ${packing.panel}` : ""}`;
                  return (
                    <div>
                      <p className="f14 bold mb-1 text-darkBlue">{name}</p>
                      <div className="row g-3 mb-3">
                        <div className="col-6">
                          <p className="f14 mb-1 text-dark sBold">
                            {t("Account")}
                          </p>
                          <Select
                            name="accountNumber"
                            filter
                            value={
                              formData[index]?.packings?.[packing.id]
                                ?.accountNumber
                            }
                            options={
                              optionData?.accountOptions?.find(
                                (item) =>
                                  item.name ===
                                    packing?.model?.category?.name ||
                                  +packing?.model?.productType === +item.name
                              )?.options
                            }
                            placeholder={t("Select")}
                            callback={(e) => handleChange(e, index, packing.id)}
                          />
                        </div>
                        <div className="col-6">
                          <p className="f14 mb-1 text-dark sBold">
                            {t("Document label (Client name)")}
                          </p>
                          <Select
                            name="clientNumber"
                            filter
                            value={
                              formData[index]?.packings?.[packing.id]
                                ?.clientNumber
                            }
                            options={optionData?.resellerCodeOptions}
                            placeholder={t("Select")}
                            callback={(e) => handleChange(e, index, packing.id)}
                          />
                        </div>
                      </div>
                      {<hr className="my-2" />}
                    </div>
                  );
                })}
                {invoice?.commande?.totalDeliveryHT > 0 && (
                  <div>
                    <p className="f14 bold mb-1 text-darkBlue">
                      {t("Delivery")}
                    </p>
                    <div className="row g-3 mb-3">
                      <div className="col-6">
                        <p className="f14 mb-1 text-dark sBold">
                          {t("Account")}
                        </p>
                        <Select
                          name="accountNumber"
                          filter
                          value={formData[index]?.delivery?.accountNumber || ""}
                          options={
                            optionData?.accountOptions?.find(
                              (item) => item.name === "Transport"
                            )?.options
                          }
                          placeholder={t("Select")}
                          callback={(e) =>
                            handleChangeTotal(e, "delivery", invoice.id)
                          }
                        />
                      </div>
                      <div className="col-6">
                        <p className="f14 mb-1 text-dark sBold">
                          {t("Document label (Client name)")}
                        </p>
                        <Select
                          name="clientNumber"
                          filter
                          value={formData[index]?.delivery?.clientNumber || ""}
                          options={optionData?.resellerCodeOptions}
                          placeholder={t("Select")}
                          callback={(e) =>
                            handleChangeTotal(e, "delivery", invoice.id)
                          }
                        />
                      </div>
                    </div>
                    {<hr className="my-2" />}
                  </div>
                )}
                <div className="">
                  <p className="f14 bold mb-1 text-darkBlue">{t("TVA")}</p>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <p className="f14 mb-1 text-dark sBold">{t("Account")}</p>
                      <Select
                        name="accountNumber"
                        filter
                        value={formData[index]?.tva?.accountNumber || ""}
                        options={
                          optionData?.accountOptions?.find(
                            (item) => item.name === "TVA"
                          )?.options
                        }
                        placeholder={t("Select")}
                        callback={(e) =>
                          handleChangeTotal(e, "tva", invoice.id)
                        }
                      />
                    </div>
                    <div className="col-6">
                      <p className="f14 mb-1 text-dark sBold">
                        {t("Document label (Client name)")}
                      </p>
                      <Select
                        name="clientNumber"
                        filter
                        value={formData[index]?.tva?.clientNumber || ""}
                        options={optionData?.resellerCodeOptions}
                        placeholder={t("Select")}
                        callback={(e) =>
                          handleChangeTotal(e, "tva", invoice.id)
                        }
                      />
                    </div>
                  </div>
                </div>
                {<hr className="my-2" />}
                <div className="">
                  <p className="f14 bold mb-1 text-darkBlue">{t("Total")}</p>
                  <div className="row g-3 mb-3">
                    <div className="col-4">
                      <p className="f14 mb-1 text-dark sBold">{t("Account")}</p>
                      <Select
                        name="accountNumber"
                        filter
                        value={formData[index]?.total?.accountNumber || ""}
                        options={[formData[index]?.total?.accountNumber]}
                        placeholder={t("Select")}
                        callback={(e) =>
                          handleChangeTotal(e, "total", invoice.id)
                        }
                      />
                    </div>
                    <div className="col-4">
                      <p className="f14 mb-1 text-dark sBold">
                        {t("Subsidiary account")}
                      </p>
                      <Select
                        name="resellerNumber"
                        filter
                        value={formData[index]?.total?.resellerNumber || ""}
                        options={optionData?.resellerCodeOptions}
                        placeholder={t("Select")}
                        callback={(e) =>
                          handleChangeTotal(e, "total", invoice.id)
                        }
                      />
                    </div>
                    <div className="col-4">
                      <p className="f14 mb-1 text-dark sBold">
                        {t("Document label (Client name)")}
                      </p>
                      <Select
                        name="clientNumber"
                        filter
                        value={formData[index]?.total?.clientNumber || ""}
                        options={optionData?.resellerCodeOptions}
                        placeholder={t("Select")}
                        callback={(e) =>
                          handleChangeTotal(e, "total", invoice.id)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="d-flex justify-content-center gap-3 mt-4">
        <Button
          onClick={handleSubmit}
          disabled={isDisabled}
          loading={isLoading}
        >
          {t("Export")}
        </Button>
        <Button onClick={() => dispatch(closeCenteredModal())}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
};

export default AccountantInvoice;
